import {
  createContext,
  FunctionComponent,
  ReactElement,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { Stack, useToast } from "@chakra-ui/react";

import Api from "src/Config";

import { Switch } from "src/components";
import { textTruncate } from "src/helpers/truncateText";
import { Tooltip } from "@chakra-ui/react";

export const RadarRulesContext = createContext(null);

type Props = {
  children: ReactElement;
};

const RadarRulesProvider: FunctionComponent<Props> = ({ children }) => {
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryParam = new URLSearchParams(search);
  const ruleType = queryParam.get("q").split("-");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [response, setResponse] = useState();

  const {
    mutateAsync,
    // data: ruleData,
    // isPending: isPendingUpdate,
    // error: errorRule,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.put("onboarding/rules", payload);
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const {
    mutateAsync: mutateRuleDelete,
    // data: ruleData,
    isPending,
    // error: errorRule,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.delete("onboarding/rules", { data: payload });
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const handleUpdateRuleAction = async (e: any, id: any) => {
    const payload = { rule_id: id, action: e.target.checked };
    try {
      const response = await mutateAsync(payload);

      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["radar_rules"] });
        toast({
          description: response.data.detail || "Rule updated successfully",
          status: "success",
          duration: 9000,
          isClosable: true,
          variant: "left-accent",
        });
      }
    } catch (error: any) {
      const err: string = error?.response?.data.detail
        ? error?.response?.data?.detail
        : "An error occurred, Please try again";
      toast({
        description: err,
        status: "error",
        duration: 9000,
        isClosable: true,
        variant: "left-accent",
      });
    }
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["radar_rules", ruleType, pageNumber],
    staleTime: 30000,
    queryFn: async () => {
      const url =
        ruleType[0] === "all"
          ? `onboarding/rules?page=${pageNumber + 1}`
          : `onboarding/rules?category=${ruleType[0]}&page=${pageNumber + 1}`;
      const { data } = await Api.get(`${url}`);

      const newResponse = data.results.map(
        (res: {
          id: string;
          predefined: boolean;
          category: string;
          name: string;
          source: string;
          created_at: string;
          email: string;
          risk_status: string;
          risk_score: number;
          max_score: number;
          condition_description: string;
          is_active: boolean;
          entity_key: string;
          entity_key_text: string;
        }) => ({
          id: res.id,
          "on/off": (
            <Stack direction={"row"}>
              <Switch
                size="sm"
                color="#251169"
                handleChange={(e: any) => handleUpdateRuleAction(e, res.id)}
                defaultChecked={res.is_active}
              />
            </Stack>
          ),
          "rule type": `${res.predefined ? "Default" : "Custom"}`,
          "rule attribute": `${
            textTruncate(res.entity_key?.replace(/_/gi, " "), 30) || "N/A"
          }`,
          condition: (
            <Tooltip
              label={res.condition_description}
              placement="right-start"
              fontSize={"12px"}
              hasArrow
            >
              {textTruncate(res.condition_description, 30) || "N/A"}
            </Tooltip>
          ),
          category: `${res.category.toLowerCase()} Rule`,
          score: res.max_score,
        }),
      );

      const response = {
        count: data.metadata.count,
        results: newResponse || [],
      };

      return response;
    },
  });

  useEffect(() => {
    if (data) {
      setResponse(data?.results);
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const handlePaginateRule = (e: any) => {
    setPageNumber(e.selected);
  };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      isFetching,
      error,
      isPending,
      mutateRuleDelete,
      handlePaginateRule,
      pageNumber,
      setPageNumber,
      pageCount,
      response,
    }),
    [
      isLoading,
      data,
      error,
      isFetching,
      isPending,
      mutateRuleDelete,
      handlePaginateRule,
      pageNumber,
      setPageNumber,
      pageCount,
      response,
    ],
  );
  return (
    <RadarRulesContext.Provider value={contextValue}>
      {children}
    </RadarRulesContext.Provider>
  );
};

export default RadarRulesProvider;
